export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

export const UTM_STORAGE_KEY = 'qi_utm_first_touch';

function readJson(storage: Storage, key: string): UtmParams {
  try {
    const raw = storage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const result: UtmParams = {};
    for (const utmKey of UTM_KEYS) {
      const value = (parsed as Record<string, unknown>)[utmKey];
      if (typeof value === 'string' && value.trim()) {
        result[utmKey] = value.trim();
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeJson(storage: Storage, key: string, value: UtmParams): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota / private mode — attribution still works for this page load.
  }
}

export function parseUtmsFromSearch(search: string): UtmParams {
  const params = new URLSearchParams(
    search.startsWith('?') ? search : `?${search}`,
  );
  const found: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value && value.trim()) {
      found[key] = value.trim();
    }
  }
  return found;
}

/**
 * First-touch UTM capture. URL params win on first sighting; later visits
 * keep the original campaign so waitlist submit still attributes to X.
 */
export function persistUtmsFromSearch(
  search: string,
  storage: Pick<Storage, 'getItem' | 'setItem'> = window.sessionStorage,
  longTerm: Pick<Storage, 'getItem' | 'setItem'> = window.localStorage,
): UtmParams {
  const fromUrl = parseUtmsFromSearch(search);
  const existing =
    Object.keys(readJson(storage as Storage, UTM_STORAGE_KEY)).length > 0
      ? readJson(storage as Storage, UTM_STORAGE_KEY)
      : readJson(longTerm as Storage, UTM_STORAGE_KEY);

  const next =
    Object.keys(existing).length > 0
      ? existing
      : fromUrl;

  if (Object.keys(fromUrl).length > 0 && Object.keys(existing).length === 0) {
    writeJson(storage as Storage, UTM_STORAGE_KEY, fromUrl);
    writeJson(longTerm as Storage, UTM_STORAGE_KEY, fromUrl);
    return fromUrl;
  }

  if (Object.keys(next).length > 0) {
    writeJson(storage as Storage, UTM_STORAGE_KEY, next);
    writeJson(longTerm as Storage, UTM_STORAGE_KEY, next);
  }

  return next;
}

export function getPersistedUtms(
  storage: Pick<Storage, 'getItem'> = window.sessionStorage,
  longTerm: Pick<Storage, 'getItem'> = window.localStorage,
): UtmParams {
  const session = readJson(storage as Storage, UTM_STORAGE_KEY);
  if (Object.keys(session).length > 0) return session;
  return readJson(longTerm as Storage, UTM_STORAGE_KEY);
}

export function emailDomain(email: string): string | undefined {
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return undefined;
  return trimmed.slice(at + 1).toLowerCase();
}
